import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  useColorModeValue,
  Badge,
} from '@chakra-ui/react';
import Header from './components/Header';
import Footer from './components/Footer';
import Editor from './components/Editor';
import Pricing from './components/Pricing';
import History from './components/History';
import APIUsageDisplay from './components/APIUsageDisplay';
import PrivacyPolicy from './components/PrivacyPolicy';
import Contact from './components/Contact';
import { GeminiApiClient } from './utils/geminiApi';
import { AIProvider } from './components/Settings';
import { EditorProvider } from './context/EditorContext';

// Gemini APIクライアントのインスタンスを作成
const geminiClient = new GeminiApiClient();

type AppPage = 'main' | 'privacy' | 'contact';

const App: React.FC = () => {
  // リリース版では全ユーザーにプレミアム機能を提供
  const [currentPlan, setCurrentPlan] = useState<'free' | 'premium'>('premium');
  const [subscriptionStatus, setSubscriptionStatus] = useState<'active' | 'inactive'>('active');
  const [apiUsage, setApiUsage] = useState<{ used: number, limit: number, remaining: number } | null>(null);
  const [tabIndex, setTabIndex] = useState(0);
  const [currentProvider, setCurrentProvider] = useState<AIProvider>('gemini-free');
  const [currentPage, setCurrentPage] = useState<AppPage>('main');

  // カラーモード対応
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const tabsBgColor = useColorModeValue('white', 'gray.800');
  const tabListBgColor = useColorModeValue('gray.100', 'gray.700');
  const selectedTabBg = useColorModeValue('white', 'gray.800');

  // API使用量の取得とサブスクリプション状態の初期化
  useEffect(() => {
    const fetchApiUsage = async () => {
      try {
        const usage = await geminiClient.getUsage();
        setApiUsage(usage);
      } catch (error) {
        console.error('API使用量の取得に失敗しました:', error);
      }
    };

    // ローカルストレージからサブスクリプション状態を復元
    const savedSubscriptionStatus = localStorage.getItem('subscriptionStatus');
    if (savedSubscriptionStatus === 'active') {
      setSubscriptionStatus('active');
      setCurrentPlan('premium');
    }

    // AI設定を読み込み
    const savedAISettings = localStorage.getItem('aiSettings');
    if (savedAISettings) {
      try {
        const settings = JSON.parse(savedAISettings);
        setCurrentProvider(settings.provider);
      } catch (error) {
        console.error('Failed to load AI settings:', error);
      }
    }

    fetchApiUsage();
    
    // 実際の実装では定期的に更新する
    // const intervalId = setInterval(fetchApiUsage, 60000);
    // return () => clearInterval(intervalId);
  }, []);



  const renderCurrentPage = () => {
    switch (currentPage) {
      case 'privacy':
        return <PrivacyPolicy onBack={() => setCurrentPage('main')} />;
      case 'contact':
        return <Contact onBack={() => setCurrentPage('main')} />;
      default:
        return (
          <Container maxW="container.xl" py={6} flex="1">
            <EditorProvider>
            <Tabs 
              variant="enclosed" 
              colorScheme="blue" 
              index={tabIndex} 
              onChange={setTabIndex}
              bg={tabsBgColor}
              borderRadius="lg"
              shadow="md"
              overflow="hidden"
            >
              <TabList bg={tabListBgColor} px={4} pt={4}>
                <Tab _selected={{ bg: selectedTabBg, borderBottomColor: selectedTabBg }}>エディタ</Tab>
                <Tab _selected={{ bg: selectedTabBg, borderBottomColor: selectedTabBg }}>履歴</Tab>
                <Tab _selected={{ bg: selectedTabBg, borderBottomColor: selectedTabBg }}>API使用量</Tab>
                <Tab _selected={{ bg: selectedTabBg, borderBottomColor: selectedTabBg }}>
                  プラン
                  {subscriptionStatus === 'active' && (
                    <Badge ml={2} colorScheme="green" fontSize="xs">Premium</Badge>
                  )}
                </Tab>
                <Tab _selected={{ bg: selectedTabBg, borderBottomColor: selectedTabBg }}>設定</Tab>
              </TabList>
              
              <TabPanels>
                {/* エディタタブ */}
                <TabPanel p={0}>
                  <Editor isPremium={true} />
                </TabPanel>
                
                {/* 履歴タブ */}
                <TabPanel p={6}>
                  <History isPremium={true} />
                </TabPanel>
                
                {/* API使用量タブ */}
                <TabPanel p={6}>
                  <APIUsageDisplay 
                    currentProvider={currentProvider}
                    onProviderChange={setCurrentProvider}
                  />
                </TabPanel>
                
                {/* プランタブ */}
                <TabPanel p={6}>
                  <Pricing />
                </TabPanel>
                
                {/* 設定タブ */}
                <TabPanel p={6}>
                  <Box borderWidth="1px" borderRadius="lg" p={6} bg={useColorModeValue('white', 'gray.700')}>
                    <Box textAlign="center" py={10}>
                      <Box fontSize="lg" mb={4}>
                        この機能はプロトタイプでは実装されていません
                      </Box>
                    </Box>
                  </Box>
                </TabPanel>
              </TabPanels>
            </Tabs>
            </EditorProvider>
          </Container>
        );
    }
  };

  return (
    <Box minH="100vh" bg={bgColor} display="flex" flexDirection="column">
      <Header apiUsage={apiUsage} currentPlan={currentPlan} />
      
      {renderCurrentPage()}
      
      <Footer onNavigate={setCurrentPage} />
    </Box>
  );
};

export default App;
